import { useCallback, useMemo } from 'react';

import { InlineAlert } from '@rbilabs/universal-components';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { FlatList, StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { CardModalHeader } from 'components/card-modal-header';
import {
  CenteredRowContainer,
  centeredRowContainerStyles,
} from 'components/ui/centered-row-container';
import { useModalAnalyticsAppearanceEvent } from 'hooks/use-modal-analytics-appearance-event';
import { useNativeTabBarContentInset } from 'hooks/use-native-tab-bar-content-inset';
import { useLoyaltyIncentivesAvailabilityUtils } from 'pages/loyalty/loyalty-incentives-components/use-loyalty-incentives-availability';
import { selectors, useAppSelector } from 'state/global-state';
import { t } from 'state/intl/translations';
import { useLoyaltyContext } from 'state/loyalty';
import { useLoyaltyOfferGroupsById } from 'state/loyalty/hooks/use-loyalty-offer-groups-by-id';
import { AppOffer, SanityId, appOfferToLoyaltyOffer } from 'state/loyalty/types';
import { spacing } from 'styles/constants';
import { WEB_SMALL_MAX_WIDTH } from 'utils/constants';
import { routes } from 'utils/routing';
import { blockContentToPlainText } from 'utils/sanity';

import { LoadingAnimation } from '../loyalty-claim-points/loyalty-claim-points.styled';

import { renderOfferItem } from './loyalty-list-item';
import { orderOffersBySanity } from './utils';

export const OffersGroupsDetails: React.FC = () => {
  const { offerGroupId, loyaltyEngineId, olp } = useLocalSearchParams<{
    offerGroupId?: SanityId;
    loyaltyEngineId?: string;
    olp?: string;
  }>();

  const { setAddOffersCallbackUrl } = useLoyaltyContext();
  // This screen renders the "offers inside a group" UI.
  //
  // There are 2 kinds of groups a user can tap from the Offers list:
  // - Systemwide Offer Group: a CMS-backed container that can be fetched by `cmsId`.
  // - Personalized Offer Group: an engine-assigned container whose header *may* be derived
  //   from a source CMS Offer Group, but whose *child offers* come from the user's
  //   personalized assignment.
  //
  // Both navigate here using `offerGroupId` in the route, so this screen chooses the
  // correct data source based on what is available in Redux.
  const appPersonalizedOfferGroups = useAppSelector(
    selectors.loyalty.selectAppPersonalizedOfferGroups
  );
  const personalizedOfferGroup = useMemo(() => {
    if (!offerGroupId) {
      return undefined;
    }
    // Route params are strings; our IDs are branded types, so normalize before comparing.
    return appPersonalizedOfferGroups.find(group => {
      // If loyaltyEngineId is provided, match by unique id
      if (loyaltyEngineId) {
        return String(group.id) === String(loyaltyEngineId);
      }
      // Fallback to cmsId match (for backward compatibility or systemwide groups)
      return String(group.cmsId) === String(offerGroupId);
    });
  }, [appPersonalizedOfferGroups, offerGroupId, loyaltyEngineId]);

  const {
    offers: systemwideOffers,
    groupName: systemwideGroupName,
    canOnlyChooseOne: systemwideCanOnlyChooseOne,
    loading: systemwideOffersLoading,
    sanitySystemideOffers,
  } = useLoyaltyOfferGroupsById(offerGroupId);
  const { getIncentiveUnavailableMessage } = useLoyaltyIncentivesAvailabilityUtils();

  // The list UI can render both offer types using `renderOfferItem`, so we unify the
  // source as `AppOffer[]`.
  const offers = useMemo(() => {
    if (!sanitySystemideOffers?.length) {
      return [];
    }

    const offersToOrder = personalizedOfferGroup?.offers?.length
      ? personalizedOfferGroup.offers.map(offer => ({
          ...offer,
          inlineAlertMessage:
            offer.inlineAlertMessage ??
            getIncentiveUnavailableMessage({
              incentive: appOfferToLoyaltyOffer(offer),
            }),
        }))
      : systemwideOffers;

    return orderOffersBySanity<AppOffer>(offersToOrder, sanitySystemideOffers);
  }, [
    getIncentiveUnavailableMessage,
    sanitySystemideOffers,
    personalizedOfferGroup?.offers,
    systemwideOffers,
  ]);

  const groupName = personalizedOfferGroup?.name
    ? blockContentToPlainText(personalizedOfferGroup.name?.localeRaw)
    : systemwideGroupName;
  const canOnlyChooseOne = personalizedOfferGroup?.canOnlyChooseOne ?? systemwideCanOnlyChooseOne;
  const offersLoading = personalizedOfferGroup ? false : systemwideOffersLoading;
  const router = useRouter();

  const handleOfferPress = useCallback(
    (incentive: AppOffer) => {
      router.navigate({
        pathname: '/offers/[loyaltyEngineId]',
        params: {
          loyaltyEngineId: incentive.loyaltyEngineId,
          ...(offerGroupId ? { groupId: offerGroupId } : {}),
          ...(olp ? { olp } : {}),
        },
      });
    },
    [olp, offerGroupId, router]
  );

  const renderItem = ({ item }: { item: AppOffer }) => {
    return renderOfferItem({
      item,
      handlePress: () => {
        if (item?.inlineAlertMessage) {
          return;
        }
        setAddOffersCallbackUrl(`${routes.loyaltyOfferGroup}/${offerGroupId}`);
        handleOfferPress(item);
      },
      skipAnimation: true,
    });
  };
  useModalAnalyticsAppearanceEvent('Offers Group Details');

  const { top } = useSafeAreaInsets();
  const nativeTabBarContentInset = useNativeTabBarContentInset();
  const headerText = groupName || t('selectOffer');

  return (
    <View testID="offers-group-items" style={{ flex: 1, paddingTop: top - 16 }}>
      <CenteredRowContainer standardPadding>
        <CardModalHeader
          {...{
            headerText,
            dismissType: 'back',
            containerStyle: { borderTopLeftRadius: 0, borderTopRightRadius: 0 },
          }}
        />
      </CenteredRowContainer>
      {canOnlyChooseOne && (
        <InlineAlert
          status="info"
          marginBottom={'$4'}
          message={
            <>
              {t('canOnlyChooseOneOfferLine1')}
              {'\n'}
              {t('canOnlyChooseOneOfferLine2')}
            </>
          }
        />
      )}
      {offersLoading ? (
        <LoadingAnimation />
      ) : (
        <FlatList
          data={offers}
          contentContainerStyle={[
            styles.flatList,
            nativeTabBarContentInset ? { paddingBottom: nativeTabBarContentInset } : null,
          ]}
          keyExtractor={(item, index) => item.cmsId || item.loyaltyEngineId || `${index}`}
          renderItem={renderItem}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  flatList: {
    ...centeredRowContainerStyles.content,
    maxWidth: WEB_SMALL_MAX_WIDTH,
    paddingHorizontal: spacing.$4,
    paddingTop: spacing.$4,
  },
});
